import React, { useState, useEffect } from 'react';
import { 
  ThumbsUp, Share2, ChevronRight, User, Mail, Phone, MapPin,
  ArrowLeft, Anchor, Calendar, AlertTriangle, CheckCircle
} from 'lucide-react';
import { issuesApi } from '../../services/issuesApi';
import StatusBadge from './StatusBadge';
import CommentSection from './CommentSection';
import './Issues.css';

export default function IssueDetail({ issueId: propIssueId, onNavigate }) {
  const id = propIssueId;
  const [issue, setIssue] = useState(null);
  
  // Navigation helper
  const navigate = (path) => {
    if (onNavigate) {
      if (path === '/issues') {
        onNavigate('issues');
      }
    }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Upvote state
  const [hasUpvoted, setHasUpvoted] = useState(false);
  const [upvoting, setUpvoting] = useState(false);
  const [upvoteAnimating, setUpvoteAnimating] = useState(false);
  
  // Share state
  const [copied, setCopied] = useState(false);
  
  // Image lightbox
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    loadIssue();
    checkUpvoteStatus();
  }, [id]);

  const loadIssue = async () => {
    try {
      setLoading(true);
      const data = await issuesApi.getIssue(id);
      setIssue(data);
    } catch (err) {
      console.error('Error loading issue:', err);
      setError('Issue not found or failed to load.');
    } finally {
      setLoading(false);
    }
  };

  const checkUpvoteStatus = async () => {
    try {
      const data = await issuesApi.getUpvoteStatus(id);
      setHasUpvoted(data.hasUpvoted);
    } catch (err) {
      console.error('Error checking upvote status:', err);
    }
  };

  const handleUpvote = async () => {
    if (hasUpvoted || upvoting) return;
    
    try {
      setUpvoting(true);
      setUpvoteAnimating(true);
      const result = await issuesApi.upvoteIssue(id);
      setIssue(prev => ({ ...prev, upvotes: result.upvotes }));
      setHasUpvoted(true);
      setTimeout(() => setUpvoteAnimating(false), 400);
    } catch (err) {
      if (err.response?.status === 400) {
        setHasUpvoted(true);
      }
      console.error('Error upvoting:', err);
    } finally {
      setUpvoting(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: issue.title,
          text: `Check out this issue: ${issue.title}`,
          url
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(url);
        }
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="issue-detail-page">
        <div className="empty-state">
          <div className="loading-spinner-small" style={{ margin: '0 auto' }} />
          <p>Loading issue...</p>
        </div>
      </div>
    );
  }

  if (error || !issue) {
    return (
      <div className="issue-detail-page">
        <div className="empty-state">
          <AlertTriangle size={60} />
          <h3>Issue Not Found</h3>
          <p>{error || 'The issue you\'re looking for doesn\'t exist.'}</p>
          <button 
            className="btn-primary" 
            onClick={() => navigate('/issues')}
            style={{ marginTop: '1rem' }}
          >
            Back to Issues
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="issue-detail-page">
      {/* Header */}
      <div className="issue-detail-header">
        <div className="issue-detail-breadcrumb">
          <button 
            onClick={() => navigate('/issues')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'underline' }}
          >
            Issues
          </button>
          <ChevronRight size={16} />
          <span>{issue.id}</span>
        </div>
        <div className="issue-detail-top">
          <div>
            <div className="issue-detail-id">{issue.id}</div>
            <h1 className="issue-detail-title">{issue.title}</h1>
          </div>
          <StatusBadge status={issue.status} />
        </div>
      </div>

      {/* Content */}
      <div className="issue-detail-content">
        {/* Main Content */}
        <div className="issue-detail-main">
          {/* Meta Info */}
          <div className="issue-detail-section">
            <h3>Details</h3>
            <div className="issue-detail-badges">
              <span className="category-badge">{issue.category}</span>
              {issue.boatModel && (
                <span className="model-badge">
                  <Anchor size={12} />
                  {issue.year ? `${issue.year} ` : ''}{issue.boatModel}
                </span>
              )}
              <span className="issue-stat">
                <Calendar size={14} />
                {formatDate(issue.timestamp)}
              </span>
            </div>
          </div>

          {/* Images */}
          {issue.imageURLs && issue.imageURLs.length > 0 && (
            <div className="issue-detail-section">
              <h3>Images</h3>
              <div className="issue-gallery">
                {issue.imageURLs.map((url, idx) => (
                  <img 
                    key={idx}
                    src={url} 
                    alt={`Issue image ${idx + 1}`}
                    className="gallery-image"
                    onClick={() => setLightboxImage(url)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="issue-detail-section">
            <h3>Description</h3>
            <div className="issue-detail-description">
              {issue.description}
            </div>
          </div>

          {/* Contact Info */}
          <div className="issue-detail-section">
            <h3>Contact Information</h3>
            <div className="issue-detail-contact">
              <div className="contact-item">
                <User size={18} />
                <div>
                  <div className="contact-item-label">Name</div>
                  <div className="contact-item-value">{issue.contactName}</div>
                </div>
              </div>
              <div className="contact-item">
                <Mail size={18} />
                <div>
                  <div className="contact-item-label">Email</div>
                  <div className="contact-item-value">{issue.contactEmail}</div>
                </div>
              </div>
              {issue.phone && (
                <div className="contact-item">
                  <Phone size={18} />
                  <div>
                    <div className="contact-item-label">Phone</div>
                    <div className="contact-item-value">{issue.phone}</div>
                  </div>
                </div>
              )}
              {issue.dealerLocation && (
                <div className="contact-item">
                  <MapPin size={18} />
                  <div>
                    <div className="contact-item-label">Location</div>
                    <div className="contact-item-value">{issue.dealerLocation}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Resolution Notes */}
          {issue.resolutionNotes && (
            <div className="issue-detail-section" style={{ background: '#E5FFE5', margin: '0 -2rem', padding: '1.5rem 2rem' }}>
              <h3 style={{ color: '#228B22' }}>
                <CheckCircle size={18} style={{ marginRight: '0.5rem' }} />
                Resolution
              </h3>
              <div className="issue-detail-description">
                {issue.resolutionNotes}
              </div>
            </div>
          )}

          {/* Comments */}
          <CommentSection issueId={id} />
        </div>

        {/* Sidebar */}
        <aside className="issue-detail-sidebar">
          {/* Back Button */}
          <button 
            className="btn-secondary" 
            onClick={() => navigate('/issues')}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            <ArrowLeft size={18} />
            Back to Issues
          </button>

          {/* Upvote Card */}
          <div className="sidebar-card upvote-section">
            <h3>Support This Issue</h3>
            <button 
              className={`upvote-button ${hasUpvoted ? 'upvoted' : ''} ${upvoteAnimating ? 'animating' : ''}`}
              onClick={handleUpvote}
              disabled={hasUpvoted || upvoting}
            >
              <ThumbsUp size={24} />
              <div className="upvote-info">
                <div className="upvote-count">{issue.upvotes || 0}</div>
                <div className="upvote-label">
                  {hasUpvoted ? 'Upvoted!' : 'Upvotes'}
                </div>
              </div>
            </button>
            <button 
              className={`share-button ${copied ? 'copied' : ''}`}
              onClick={handleShare}
            >
              <Share2 size={16} />
              {copied ? 'Link Copied!' : 'Share Issue'}
            </button>
          </div>

          {/* Issue Info Card */}
          <div className="sidebar-card">
            <h3>Issue Information</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.9rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#999' }}>Status</span>
                <StatusBadge status={issue.status} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#999' }}>Category</span>
                <span style={{ fontWeight: '500' }}>{issue.category}</span>
              </div>
              {issue.boatModel && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#999' }}>Model</span>
                  <span style={{ fontWeight: '500' }}>{issue.boatModel}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#999' }}>Comments</span>
                <span style={{ fontWeight: '500' }}>{issue.commentCount || 0}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* Image Lightbox */}
      {lightboxImage && (
        <div 
          className="success-modal-overlay"
          onClick={() => setLightboxImage(null)}
        >
          <img 
            src={lightboxImage} 
            alt="Full size"
            style={{ 
              maxWidth: '90vw', 
              maxHeight: '90vh', 
              borderRadius: '8px',
              objectFit: 'contain'
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

